import {
  ENVS,
  getApigeeUrl,
  getEnv,
  getRegion,
  REGIONS,
} from '@adsk/acs-sc-web-platform-api';
import PermissionSelect from '@adsk/alloy-react-permission-select';
import { SERVICE_ACCESSES } from '@adsk/acs-admin-ui-components-helpers';
import {
  NewIssuePermissionLevel,
  OldIssuePermissionLevel,
} from '../../constants';
import { getPillValue } from './utils';

const US_CONFIG = {
  [ENVS.QA]: `${getApigeeUrl()}/construction/issues-qa`,
  [ENVS.STAGING]: `${getApigeeUrl()}/construction/issues-stg`,
  [ENVS.PRODUCTION]: `${getApigeeUrl()}/construction/issues`,
};

const ENV_CONFIGS = {
  [REGIONS.US]: US_CONFIG,
  [REGIONS.EMEA]: US_CONFIG,
  [REGIONS.APAC]: US_CONFIG,
};

export const oldPermissionLevelOrder = {
  [OldIssuePermissionLevel.createForMyCompany]: 1,
  [OldIssuePermissionLevel.createForOtherCompanies]: 2,
  [OldIssuePermissionLevel.manageIssues]: 3,
  [OldIssuePermissionLevel.manageMemberPermissions]: 4,
};

export const newPermissionLevelOrder = {
  [NewIssuePermissionLevel.viewEditAndAssignMyCompany]: 1,
  [NewIssuePermissionLevel.viewEditAndAssignAll]: 2,
  [NewIssuePermissionLevel.fullVisibility]: 3,
  [NewIssuePermissionLevel.manageIssues]: 4,
  [NewIssuePermissionLevel.administrator]: 5,
};

export const getPermissionLevelOrder = (
  shouldUseNewPermissions,
  permissionLevel
) => {
  if (!permissionLevel) {
    return -1;
  }
  if (shouldUseNewPermissions) {
    return newPermissionLevelOrder[permissionLevel] || -1;
  }
  return oldPermissionLevelOrder[permissionLevel] || -1;
};

/**
 * Builds the request for getting issues permissions
 * @param {string} projectId - The project id
 * @returns {string} - The request url
 */
export const buildGetIssuesPermissionsRequest = (projectId) => {
  const env = getEnv();
  const region = getRegion();

  return `${ENV_CONFIGS[region][env]}/v1/projects/${projectId}/permissions`;
};

const getIssuesSelectOptions = (
  T,
  overridePermissionInfo,
  shouldUseNewIssuePermissions
) => {
  const permissionsToIterateOver = shouldUseNewIssuePermissions
    ? NewIssuePermissionLevel
    : OldIssuePermissionLevel;
  return Object.values(permissionsToIterateOver).reduce(
    (result, permissionLevel) => {
      if (
        permissionLevel === OldIssuePermissionLevel.manageMemberPermissions ||
        permissionLevel === NewIssuePermissionLevel.administrator
      )
        return result;
      const disabled =
        !!overridePermissionInfo &&
        getPermissionLevelOrder(shouldUseNewIssuePermissions, permissionLevel) <
          getPermissionLevelOrder(
            shouldUseNewIssuePermissions,
            overridePermissionInfo.permissionLevel
          );
      const pillValue = getPillValue(permissionLevel);
      const permissionDescription = T.translate(
        `PERMISSIONS.ISSUES.${
          shouldUseNewIssuePermissions
            ? 'NEW_LEVEL_DESCRIPTIONS'
            : 'OLD_LEVEL_DESCRIPTIONS'
        }.${permissionLevel}`
      );
      const option = {
        label: T.translate(
          `PERMISSIONS.ISSUES.${
            shouldUseNewIssuePermissions
              ? 'NEW_PERMISSION_LEVELS'
              : 'OLD_PERMISSION_LEVELS'
          }.${permissionLevel}`
        ),
        value: `header-${permissionLevel}`,
        pillValue,
        options: [
          {
            value: permissionLevel,
            label: permissionDescription,
            pillValue,
            disabled,
            tooltip:
              disabled && !!overridePermissionInfo
                ? T.translate('PERMISSIONS.ISSUES.DISABLED_DESCRIPTION', {
                    level: overridePermissionInfo.levelName,
                    subjectType: overridePermissionInfo.subjectType,
                    subjectName: overridePermissionInfo.subjectName,
                  })
                : permissionDescription,
          },
        ],
      };
      result.push(option);

      return result;
    },
    []
  );
};

const getLevelLabel = (permissionLevel, T) => {
  switch (permissionLevel) {
    case OldIssuePermissionLevel.createForMyCompany:
    case OldIssuePermissionLevel.createForOtherCompanies:
    case OldIssuePermissionLevel.manageIssues:
    case OldIssuePermissionLevel.manageMemberPermissions:
      return T.translate(
        `PERMISSIONS.ISSUES.OLD_PERMISSION_LEVELS.${permissionLevel}`
      );
    case NewIssuePermissionLevel.viewEditAndAssignMyCompany:
    case NewIssuePermissionLevel.viewEditAndAssignAll:
    case NewIssuePermissionLevel.fullVisibility:
    case NewIssuePermissionLevel.manageIssues:
      return T.translate(
        `PERMISSIONS.ISSUES.NEW_PERMISSION_LEVELS.${permissionLevel}`
      );
    default:
      return '';
  }
};

const getIssuesInheritedPermissionData = (
  memberPermissionLevel,
  companyPermissionsLevel,
  rolePermissionsLevel,
  selectedCompanyName,
  selectedRoleName,
  selectedAccessLevel,
  T,
  shouldUseNewIssuePermissions
) => {
  if (selectedAccessLevel === SERVICE_ACCESSES.ADMIN) {
    return {
      permissionLevel: shouldUseNewIssuePermissions ? NewIssuePermissionLevel.administrator : OldIssuePermissionLevel.manageMemberPermissions,
      levelName: T.translate(shouldUseNewIssuePermissions ?
        `PERMISSIONS.ISSUES.NEW_PERMISSION_LEVELS.administrator` :
          `PERMISSIONS.ISSUES.OLD_PERMISSION_LEVELS.manage-member-permissions`
      ),
      subjectType: T.translate('PERMISSIONS.TYPES.ROLE_TEXT'),
      subjectName: 'Admin',
    };
  }

  let permissionLevel = memberPermissionLevel;
  let subjectName;
  let subjectType;

  if (
    getPermissionLevelOrder(shouldUseNewIssuePermissions, permissionLevel) <
    getPermissionLevelOrder(
      shouldUseNewIssuePermissions,
      companyPermissionsLevel
    )
  ) {
    permissionLevel = companyPermissionsLevel;
    subjectType = T.translate('PERMISSIONS.TYPES.COMPANY_TEXT');
    subjectName = selectedCompanyName;
  }

  if (
    getPermissionLevelOrder(shouldUseNewIssuePermissions, permissionLevel) <
    getPermissionLevelOrder(shouldUseNewIssuePermissions, rolePermissionsLevel)
  ) {
    permissionLevel = rolePermissionsLevel;
    subjectType = T.translate('PERMISSIONS.TYPES.ROLE_TEXT');
    subjectName = selectedRoleName;
  }

  return getPermissionLevelOrder(
    shouldUseNewIssuePermissions,
    permissionLevel
  ) !==
    getPermissionLevelOrder(shouldUseNewIssuePermissions, memberPermissionLevel)
    ? {
        permissionLevel,
        levelName: getLevelLabel(permissionLevel, T),
        subjectType,
        subjectName,
      }
    : null;
};

export const getHighestRolePermissionLevel = (
  rolesPermissions,
  rolesInfo,
  shouldUseNewPermissionLevels
) => {
  let highestPermissionLevel = null;
  let highestRoleName = null;
  rolesPermissions.forEach((rolePermission) => {
    const { permissionLevel: currentPermissionLevel } = rolePermission;
    if (
      rolePermission.permissionLevel &&
      getPermissionLevelOrder(
        shouldUseNewPermissionLevels,
        currentPermissionLevel
      ) >
        getPermissionLevelOrder(
          shouldUseNewPermissionLevels,
          highestPermissionLevel
        )
    ) {
      highestPermissionLevel = currentPermissionLevel;
      highestRoleName = rolesInfo.find(
        (info) => info.memberGroupId === rolePermission.subject
      )?.name;
    }
  });
  return {
    roleName: highestRoleName,
    permissionLevel: highestPermissionLevel,
  };
};
export const getIssuesPermissionsInfo = (
  memberPermissions,
  companyPermissions,
  rolesPermissions,
  selectedAccessLevel,
  selectedCompany,
  selectedRoles,
  T,
  shouldUseNewIssuePermissions
) => {
  const memberPermissionLevel =
    memberPermissions?.permissionLevel ||
    (shouldUseNewIssuePermissions
      ? NewIssuePermissionLevel.viewEditAndAssignMyCompany
      : OldIssuePermissionLevel.createForMyCompany);
  const companyPermissionLevel = companyPermissions?.permissionLevel;
  const { permissionLevel: rolePermissionLevel, roleName } =
    getHighestRolePermissionLevel(
      rolesPermissions,
      selectedRoles,
      shouldUseNewIssuePermissions
    );
  const inheritedPermissionData = getIssuesInheritedPermissionData(
    memberPermissionLevel,
    companyPermissionLevel,
    rolePermissionLevel,
    selectedCompany?.name,
    roleName,
    selectedAccessLevel,
    T,
    shouldUseNewIssuePermissions
  );
  const inheritedPermissions = !!inheritedPermissionData;
  let inheritanceInfo;
  let permissionsIndicatorProps;
  if (inheritedPermissions) {
    const { permissionLevel, levelName, subjectType, subjectName } =
      inheritedPermissionData;
    inheritanceInfo = T.translate('INHERITED_PERMISSIONS_INFO', {
      subjectType,
      subjectName,
    });
    permissionsIndicatorProps = {
      label: levelName,
      pillValue: getPillValue(permissionLevel),
    };
  }

  return {
    selectValue: memberPermissionLevel && { value: memberPermissionLevel },
    permissionsIndicatorProps,
    inheritedPermissions,
    inheritanceInfo,
    selectOptions: getIssuesSelectOptions(
      T,
      inheritedPermissionData,
      shouldUseNewIssuePermissions
    ),
    selectType: PermissionSelect.displayName,
    selectValueLabel: getLevelLabel(memberPermissionLevel, T),
    pillValue: getPillValue(memberPermissionLevel),
  };
};
